import React from "react";

/**
 * Row builders shared by the admin, client and interpreter appointment views.
 * Each returns [{ label, value, hide }] for <DetailCard>. All take (item, t).
 */

const money = (v) => `$${Number(v ?? 0).toFixed(2)}`;

export function toCapitalize(val) {
  return <span className="text-capitalize">{val}</span>;
}

export const yesNo = (value, t) => (Number(value) === 1 ? t("common.yes") : t("common.no"));

export const parseDynamicFields = (item) => {
  const raw = item?.appointment_details?.dynamic_fields;
  if (!raw) return [];
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    return (Array.isArray(parsed) ? parsed : []).filter((f) => f && f.name);
  } catch {
    return [];
  }
};

export const emailList = (value) => {
  if (!value) return null;
  const parts = String(value)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length === 0) return null;
  return (
    <span className="appt-list">
      {parts.map((email) => (
        <span key={email}>{email}</span>
      ))}
    </span>
  );
};

export const addressOf = (item) => {
  const d = item?.appointment_details;
  if (!d) return null;
  if (d.address) return d.address;
  const f = d.facility;
  if (!f) return null;
  const line1 = f.street_address;
  const line2 = [f.city_name, [f.state?.name, f.zipcode].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  if (!line1 && !line2) return null;
  return (
    <span className="appt-list">
      {line1 && <span>{line1}</span>}
      {line2 && <span>{line2}</span>}
    </span>
  );
};

/** Company vendor vs the person actually interpreting. */
export const vendorNames = (item) => {
  const i = item?.interpreter;
  if (!i) return { vendor: null, interpreter: null };
  const person = i.user?.name || [i.first_name, i.last_name].filter(Boolean).join(" ") || null;
  const linked = i.linked_interpreter;
  if (Number(i.is_company) === 1) {
    const linkedName = linked?.user?.name || [linked?.first_name, linked?.last_name].filter(Boolean).join(" ") || null;
    return { vendor: person, interpreter: linkedName };
  }
  if (linked && Number(linked.is_company) === 1) {
    return { vendor: linked.user?.name || null, interpreter: person };
  }
  return { vendor: person, interpreter: person };
};

export const locationRows = (item, t) => {
  const d = item?.appointment_details || {};
  return [
    { label: t("appointmentDetail.facility"), value: d.facility?.facility_name },
    { label: t("appointmentDetail.department"), value: d.department?.department_name || d.department_text },
    { label: t("appointmentDetail.address"), value: addressOf(item), hide: item?.type !== "In Person" && !d.address },
  ];
};

export const requesterRows = (item, t) => {
  const d = item?.appointment_details || {};
  return [
    { label: t("appointmentDetail.requesterName"), value: d.requester_name },
    { label: t("appointmentDetail.requesterEmail"), value: emailList(d.requester_email) },
    { label: t("appointmentDetail.requesterPhone"), value: d.requester_phone },
  ];
};

/** Labels always render; values fall back to N/A when no vendor is assigned. */
export const vendorRows = (item, t, { contact = true } = {}) => {
  const i = item?.interpreter;
  const { vendor, interpreter } = vendorNames(item);
  return [
    { label: t("appointmentDetail.vendorName"), value: vendor },
    { label: t("appointmentView.interpreterName"), value: interpreter, hide: !!i && (!interpreter || interpreter === vendor) },
    { label: t("appointmentView.vendorCode"), value: i?.code },
    { label: t("appointmentDetail.vendorEmail"), value: i?.user?.email, hide: !contact },
    { label: t("appointmentDetail.vendorPhone"), value: i?.phone, hide: !contact },
  ];
};

/** Only the requirement flags that are set show, so the card stays short. */
export const requirementRows = (item, t) => {
  const d = item?.appointment_details || {};
  const genderKey = d.interpreter_gender ? `gender.${String(d.interpreter_gender).toLowerCase()}` : null;
  const flag = (key, field) => ({ label: t(key), value: t("common.yes"), hide: Number(d[field]) !== 1 });
  return [
    flag("appointmentDetail.usBasedVendor", "us_based"),
    flag("appointmentDetail.nonUsBasedVendor", "non_us_based"),
    flag("appointmentDetail.englishToTarget", "english_to_target"),
    flag("appointmentDetail.spanishToTarget", "spanish_to_target"),
    flag("appointmentDetail.courtCertified", "court_certified"),
    flag("appointmentDetail.medicalCertified", "medical_certified"),
    { label: t("appointmentView.interpreterGender"), value: genderKey ? t(genderKey, { defaultValue: d.interpreter_gender }) : t("appointmentView.noPreference") },
    { label: t("appointmentView.subject"), value: item?.subject, hide: !item?.subject },
  ];
};

/** Core facts card. `when` = portal-specific date/time rows, already formatted. */
export const appointmentRows = (item, t, { when = [], showClient = true } = {}) => {
  const d = item?.appointment_details || {};
  const duration = item?.estimated_duration ? `${item.estimated_duration} ${item.estimated_duration_unit || ""}`.trim() : null;
  return [
    { label: t("appointmentView.client"), value: item?.accounts?.user?.name, hide: !showClient },
    { label: t("appointmentView.subAccount"), value: item?.subclient?.subaccount_name, hide: !showClient || !item?.subclient?.subaccount_name },
    ...when,
    { label: t("table.language"), value: item?.language?.name },
    { label: t("appointmentForm.appointmentType"), value: item?.type },
    { label: t("appointmentView.clientNotes"), value: d.notes },
    { label: t("appointmentDetail.estimatedDuration"), value: duration },
  ];
};

/** Admin notes and the reschedule / cancel / dnc / cnc reasons. */
export const otherDetailRows = (item, t, { adminNotes = true } = {}) => {
  const d = item?.appointment_details || {};
  return [
    { label: t("appointmentDetail.appointmentNotes"), value: d.admin_notes, hide: !adminNotes },
    { label: t("appointmentDetail.rescheduledReason"), value: d.reschedule_reason },
    { label: t("appointmentDetail.cancelReason"), value: d.cancel_reason },
    { label: t("appointmentDetail.dncReason"), value: d.dnc_reason },
    { label: t("appointmentDetail.cncReason"), value: d.cnc_reason },
  ];
};

export const dynamicFieldRows = (item, t) =>
  parseDynamicFields(item).map((f) => ({ label: f.name, value: f.value }));

export const checkInOutRows = (item, t) => {
  const a = item?.appointment_assign || {};
  return [
    { label: t("appointmentDetail.checkinDate"), value: a.checkin_date },
    { label: t("appointmentDetail.checkinTime"), value: a.checkin_time },
    { label: t("appointmentDetail.checkoutDate"), value: a.checkout_date },
    { label: t("appointmentDetail.checkoutTime"), value: a.checkout_time },
    { label: t("appointmentDetail.notes"), value: a.notes },
    { label: t("appointmentView.comments"), value: a.comments, hide: !a.comments },
  ];
};

/** Admin custom pricing (appointment_custom_pricing). */
export const customPricingRows = (item, t) => {
  const p = item?.appointment_custom_pricing;
  if (!p) return [];
  return [
    { label: t("appointmentDetail.mileage"), value: p.mileage ?? "0" },
    { label: t("appointmentDetail.mileageRate"), value: money(p.mileage_rate) },
    { label: t("appointmentDetail.mileageTotal"), value: money(p.mileage_total) },
    { label: t("appointmentDetail.travelHours"), value: p.travel_hours ?? "0" },
    { label: t("appointmentDetail.travelRate"), value: money(p.travel_rate) },
    { label: t("appointmentDetail.travelTotal"), value: money(p.travel_total) },
    { label: t("appointmentDetail.minDuration"), value: p.min_duration ?? "0" },
    { label: t("appointmentDetail.ratePerHour"), value: money(p.duration_rate) },
    { label: t("appointmentDetail.sessionTotal"), value: money(p.session_total) },
    { label: t("appointmentDetail.subtotal"), value: money(p.sub_total) },
    { label: t("appointmentDetail.discount"), value: `${p.discount ?? 0}%` },
    { label: t("appointmentDetail.appointmentTotal"), value: <strong>{money(p.appointment_total)}</strong> },
  ];
};

/** Interpreter work order (appointment_interpreter_work_order). */
export const workOrderRows = (item, t) => {
  const w = item?.appointment_interpreter_work_order;
  if (!w) return [];
  return [
    { label: t("appointmentDetail.mileage"), value: w.mileage ?? "0" },
    { label: t("appointmentDetail.mileageRate"), value: money(w.mileage_rate) },
    { label: t("appointmentDetail.mileageTotal"), value: money(w.mileage_total) },
    { label: t("appointmentDetail.travelHours"), value: w.travel_hours ?? "0" },
    { label: t("appointmentDetail.travelRate"), value: money(w.travel_rate) },
    { label: t("appointmentDetail.travelTotal"), value: money(w.travel_total) },
    { label: t("appointmentDetail.minDuration"), value: w.min_duration ?? "0" },
    { label: t("appointmentDetail.ratePerHour"), value: money(w.duration_rate) },
    { label: t("appointmentDetail.sessionTotal"), value: money(w.session_total) },
    { label: t("table.total"), value: <strong>{money(w.total)}</strong> },
  ];
};

export const feedbackRows = (item, t) => {
  const f = item?.feedback;
  if (!f) return [];
  return [
    { label: t("appointmentDetail.feedbackRating"), value: f.rating ? `${f.rating} ${t("appointmentDetail.stars")}` : null },
    { label: t("appointmentDetail.feedbackComment"), value: f.comments },
    { label: t("appointmentDetail.feedbackSuggestion"), value: f.suggestions },
  ];
};

export const videoRows = (item, t, { showLink = true } = {}) => {
  const d = item?.appointment_details || {};
  if (item?.type !== "Video Remote Interpretation") return [];
  const link = d.video_link;
  return [
    { label: t("appointmentView.videoPlatform"), value: d.video_platform ? t(`appointmentView.platform.${d.video_platform}`, { defaultValue: d.video_platform }) : null },
    {
      label: t("appointmentView.videoLink"),
      hide: !showLink,
      value: link ? (
        <a href={/^https?:\/\//i.test(link) ? link : `https://${link}`} target="_blank" rel="noopener noreferrer">
          {link}
        </a>
      ) : null,
    },
  ];
};
